import React, {
	useEffect, useState, useMemo, useRef, useCallback, useContext,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import classNames from 'classnames';
import { filter, isEqual } from 'underscore';
import { GroupedVirtuoso } from 'react-virtuoso';
import { GreenroomState as GS } from '../../stores/types/types';
import * as MediaActions from '../../stores/actions/media';
import * as VideoActions from '../../stores/actions/video';
import * as GraphicsActions from '../../stores/actions/graphics';
import Icon, { ICONS, COLORS } from '../general-components/icon';
import type { MediaItem, MediaSearchResults, PresentedMediaItem } from '../../stores/types/media';
import MediaLink from './media-link';
import ScreenShareLink from './screenshare-link';
import MediaModal from './media-modal';
import Socket from '../../connection/socket';
import { ConnectedUser } from '../../stores/types/connection';
import { DeleteMediaItem, DeleteCloudConvertJob, ConvertMovToOverlay } from '../../connection/media';
import { showAlert } from '../general-components/alert-service';
import SlidingMenu, { SlidingMenuOption } from '../general-components/sliding-menu';
import { MediaTabOptionTypes } from '../../stores/types/session';
import { MEDIA_TABS, RESTRICTED_CONNECTION_TYPES } from '../../stores/reducers/session';
import Dialog from '../general-components/dialog';
import type { ShotFeed } from '../../stores/types/shots';
import { FEED_TYPES } from '../../stores/reducers/shots';
import MediaSlidesView from './media-slides-view';
import useShots from '../../contexts/shots/provider';
import usePrevious from '../../utils/use-previous';
import type { Invitation } from '../../stores/types/invitations';
import { ROOM_STATES } from '../../stores/reducers/settings';
import type { GraphicInterface, OverlayUploadData } from '../../stores/types/graphics';
import GraphicsLink from '../custom-graphics/graphics-link';
import { SHOW_GRAPHIC_MODAL, HIDE_GRAPHIC_MODAL } from '../../stores/actions/graphics';
import CustomGraphicsModal from '../custom-graphics/custom-graphics-modal';
import { buildOverlay, doUploadOverlay } from '../../utils/custom-graphics';
import { useMediaUpload } from '../../hooks/use-content-upload';
import MediaMenu from './media-menu';
import {
	buildMediaUrlPrefix,
	downloadFile,
	downloadMedia,
	getTags,
} from '../../utils/media';
import ShareList from '../general-components/share-list';
import { SHARE_TYPE } from '../general-components/share-with';
import { UploadContext, UploadInterface } from '../../contexts/upload-context';
import UploadingLink from './uploading-link';
import {
	ALL_FILE_TYPES,
	AUDIO_TYPES,
	CLOUD_CONVERT_STATUS,
	MEDIA_TYPES,
	UPLOAD_TYPES,
} from '../../types/constants';
import { ContentServiceJobStatus, FkTable } from '../../stores/types/content';
import Portal from '../general-components/portal';
import { CloudConvertUploadData } from '../../types/interfaces';
import { setActiveSubTab } from '../../stores/actions/tabs';
import { TABS } from '../../stores/types/tabs';

interface OwnProps {
	socket: Socket | null;
	openScreenShareTab: boolean;
	onScreenSharePrompt: Function;
	isForShotModal: boolean;
}

interface StopProcessingData {
	type: string;
	contentId: number;
	cloudConvertId: string;
}

const placeholderListElement = <div style={{ height: '0.5px' }}></div>;

// do not show stylepack items in the media drawers
const filterCapsule = (i: {provider?: string | null}) => (i?.provider ?? '') !== 'capsule';

export default function Media(props: OwnProps) {
	const dispatch = useDispatch();

	const {
		socket,
		openScreenShareTab,
		onScreenSharePrompt,
		isForShotModal,
	} = props;

	const media = useSelector((s: GS) => s.MediaReducer.media);
	const mediaModalOpen = useSelector((s: GS) => s.MediaReducer.mediaModalOpen);
	const currentlyUploadingMedia = useSelector((s: GS) => s.MediaReducer.currentlyUploadingMedia);
	const presentedMedia = useSelector((s: GS) => s.MediaReducer.presentedMedia);
	const shotModalShot = useSelector((s: GS) => s.ShotsReducer.shotModalShot);
	const user = useSelector((s: GS) => s.SessionReducer.user);
	const meeting = useSelector((s: GS) => s.SessionReducer.meeting);
	const token = useSelector((s: GS) => s.SessionReducer.token);
	const connectedUsers = useSelector((s: GS) => s.SessionReducer.connectedUsers);
	const host = useSelector((s: GS) => s.SessionReducer.host);
	const restrictedUserState = useSelector((s: GS) => s.SessionReducer.restrictedUserState);
	const invitations = useSelector((s: GS) => s.InvitationsReducer.invitations);
	const roomState = useSelector((s: GS) => s.MeetingReducer.roomState);
	const sharingScreen = useSelector((s: GS) => s.VideoReducer.sharingScreen);
	const baseGraphics = useSelector((s: GS) => s.GraphicsReducer.graphics);
	const featureFlags = useSelector((s: GS) => s.SettingsReducer.featureFlags);
	const showGraphicModal = useSelector((s: GS) => s.GraphicsReducer.showGraphicModal);
	const activeSubTabs = useSelector((s: GS) => s.TabsReducer.activeSubTabs);
	const generatedContent = useSelector((s: GS) => s.GeneratedContentReducer.generatedContent);
	const currentlyUploadingInModal = useSelector(
		(s: GS) => s.MediaReducer.currentlyUploadingInModal,
	);
	const idOfCurrentlyUploadingMedia = useSelector(
		(s: GS) => s.MediaReducer.idOfCurrentlyUploadingMedia,
	);
	const slideViewMedia = useSelector((s: GS) => s.MediaReducer.slideViewMedia);

	const [progress, setProgress] = useState(0);
	const [tick, setTick] = useState(0);
	const [idOfMediaBeingDownloaded, setIdOfMediaBeingDownloaded] = useState<number>(0);
	const [graphicForModal, setGraphicForModal] = useState<GraphicInterface | null>(null);
	const totalBytesToUpload = useRef(0);
	const totalUploadedBytes = useRef(0);
	const lastUploadedBytes = useRef(0);
	const dialog = useRef<any>(null);
	const optionKeyDownRef = useRef<boolean>(false);
	const fileInputRef = useRef<HTMLInputElement | null>(null);
	const fileHttpRequestRef = useRef<XMLHttpRequest | undefined>();
	// used to block the upload button from being clicked again
	const [uploading, setUploading] = useState<boolean>(false);
	const [filteredMedia, setFilteredMedia] = useState<MediaItem[]>(media.filter((mediaItem: MediaItem) => mediaItem.type !== 'hls'));
	const [filteredRecordings, setFilteredRecordings] = useState<MediaItem[]>(media.filter((mediaItem: MediaItem) => mediaItem.type === 'hls'));
	const [isMediaFiltered, setIsMediaFiltered] = useState<boolean>(false);
	const [deleteDialogXY, setDeleteDialogXY] = useState([0, 0]);
	const [shareViewOpen, setShareViewOpen] = useState<boolean>(false);
	const [shareViewHidden, setShareViewHidden] = useState<boolean>(true);
	const [selectedShareItem, setSelectedShareItem] = useState<MediaItem | null>(null);
	const [downloadingOverlayIds, setDownloadingOverlayIds] = useState<number[]>([]);

	const {
		currentShot,
		stripFeedFromShots,
		stripGraphicFromShots,
	} = useShots();
	const { doUploadMedia } = useMediaUpload(socket);

	const {
		addUpload,
		mediaUploads,
		overlayUploads,
	} = useContext(UploadContext);

	const mediaTabOptions = useMemo(() => [
		{ label: MediaTabOptionTypes.Files },
		{ label: MediaTabOptionTypes.Screenshares },
		{ label: MediaTabOptionTypes.Overlays },
		{ label: MediaTabOptionTypes.Recordings },
	], []);

	const files = useMemo(() => media?.filter((file) => file.type !== 'hls' && (
		(file.convert_status === CLOUD_CONVERT_STATUS.COMPLETE || !file.original_id)
			|| host
			|| file.created_by === user?.gr_user
		) && !file.provider
	), [media]);

	const graphicsArray = useMemo(
		() => baseGraphics.filter(filterCapsule),
		[baseGraphics]
	)

	const [filteredGraphics, setFilteredGraphics] = useState<GraphicInterface[]>(graphicsArray);

	const recordings = useMemo(() => media?.filter((recording) => recording.type === 'hls'
		&& (
			recording.convert_status === CLOUD_CONVERT_STATUS.COMPLETE
			|| host
			|| recording.created_by === user?.gr_user
		)), [media]);

	useEffect(() => {
		if (openScreenShareTab) {
			dispatch(setActiveSubTab(TABS.MEDIA, mediaTabOptions[1].label));
			onScreenSharePrompt();
		}
	}, [openScreenShareTab]);

	useEffect(() => {
		// if we are not currently searching or filtering/sorting these should be identical
		if (!isMediaFiltered) {
			if (filteredMedia !== files) {
				setFilteredMedia(files);
			}
			if (filteredRecordings !== recordings) {
				setFilteredRecordings(recordings);
			}
			if (filteredGraphics !== graphicsArray) {
				setFilteredGraphics(graphicsArray);
			}
		}

		if (isMediaFiltered) {
			// if we are currently searching and someone deleted media or graphics,
			// need to remove media from filteredMedia and graphics from filteredGraphics
			const mediaIds = files.map((media1: MediaItem) => media1.id);
			const newMediaFilteredBySearch = filteredMedia.filter(
				(media1: MediaItem) => mediaIds?.includes(media1.id),
			);
			if (newMediaFilteredBySearch.length !== filteredMedia.length) {
				setFilteredMedia(newMediaFilteredBySearch);
			}

			const recordingIds = recordings.map((recording: MediaItem) => recording.id);
			const newRecordingsFilteredBySearch = filteredRecordings.filter(
				(recording: MediaItem) => recordingIds?.includes(recording.id),
			);
			if (newRecordingsFilteredBySearch.length !== filteredRecordings.length) {
				setFilteredRecordings(newRecordingsFilteredBySearch);
			}

			const graphicIds = graphicsArray.map((graphic: GraphicInterface) => graphic.id);
			const newGraphicsFilteredBySearch = filteredGraphics.filter(
				(graphic: GraphicInterface) => graphicIds?.includes(graphic.id),
			);
			if (newGraphicsFilteredBySearch.length !== filteredGraphics.length) {
				setFilteredGraphics(newGraphicsFilteredBySearch);
			}
		}
	}, [isMediaFiltered, files, recordings, graphicsArray]);

	useEffect(() => {
		if (graphicForModal) {
			const updatedGraphicForGraphicModal = graphicsArray.find(
				(graphic: GraphicInterface) => graphic.id === graphicForModal.id,
			);
			if (updatedGraphicForGraphicModal
				&& !isEqual(graphicForModal, updatedGraphicForGraphicModal)) {
				setGraphicForModal(updatedGraphicForGraphicModal);
			}
		}
	}, [graphicForModal, graphicsArray]);

	function clearUploadProgress() {
		// note there is a dupe function in media-modal.tsx
		setUploading(false);
		setProgress(0);

		if (totalBytesToUpload.current) {
			totalBytesToUpload.current = 0;
		}

		if (totalUploadedBytes.current) {
			totalUploadedBytes.current = 0;
		}

		if (lastUploadedBytes.current) {
			lastUploadedBytes.current = 0;
		}
	}

	// If a media item is cancelled during upload to s3 (image, gif, non converted mp4, etc...),
	// We want to reset uploading state. This doesn't apply to cloud convert uploads (ppt, pdf, key)
	const prevCurrentlyUploadingMedia = usePrevious({ currentlyUploadingMedia });
	useEffect(() => {
		if (prevCurrentlyUploadingMedia?.currentlyUploadingMedia && !currentlyUploadingMedia) {
			if (!prevCurrentlyUploadingMedia.currentlyUploadingMedia.cloud_convert_id) {
				// If s3 upload cancelled, abort the XMLHttpRequest otherwise the file keeps uploading
				if (fileHttpRequestRef.current) {
					fileHttpRequestRef.current.abort();
					fileHttpRequestRef.current = undefined;
				}

				clearUploadProgress();

				// Clear the file input selection. This is necessary to prompt onChange
				// if the uploader decides to upload the same file (i.e. cancels their
				// upload and then decides to upload the same file again)
				if (fileInputRef.current) {
					fileInputRef.current.value = '';
				}
			}
		}
	}, [currentlyUploadingMedia]);

	// Had to add useRef here because in handleUpload
	// currentlyUploadingMedia was getting set but later
	// in the function that same variable was still undefined
	const currentlyUploadingMediaRef = useRef(undefined as MediaItem | undefined);

	useEffect(() => {
		currentlyUploadingMediaRef.current = currentlyUploadingMedia;
	}, [currentlyUploadingMedia]);

	function handleKeyDown(event: KeyboardEvent) {
		if (event.keyCode === 18) {
			optionKeyDownRef.current = true;
		}
	}

	function handleKeyUp(event: KeyboardEvent) {
		if (event.keyCode === 18) {
			optionKeyDownRef.current = false;
		}
	}

	useEffect(() => {
		// Used as an easter egg for producers to bypass the
		// auto conversion of high bitrate mp4s (> 4mb/s)
		document.addEventListener('keydown', handleKeyDown);
		document.addEventListener('keyup', handleKeyUp);

		return () => {
			document.removeEventListener('keydown', handleKeyDown);
			document.removeEventListener('keyup', handleKeyUp);
		};
	}, []);

	useEffect(() => {
		if (tick) {
			const interval = setInterval(() => {
				setProgress((progress1) => progress1 + tick);

				if (progress >= 100) {
					clearInterval(interval);
					setTick(0);
				}
				// make it look a little random
			}, (Math.random() * 200 + 150));

			return () => {
				clearInterval(interval);
			};
		}

		return () => {};
	}, [tick, progress]);

	// scroll back to top if you scrolled in side drawer for media in modal
	useEffect(() => {
		if (!isForShotModal) return;
		const sideDrawerObject = document.getElementById('sbm-right-drawer');
		if (sideDrawerObject) {
			sideDrawerObject.scrollTo(0, 0);
		}
	}, [activeSubTabs.media]);

	async function onOverlayUploadSuccess(
		uploadData: OverlayUploadData,
		imageUrl: string,
	): Promise<void> {
		if (meeting && user) {
			const graphic = await buildOverlay(
				uploadData.originalName,
				imageUrl,
				user.gr_user,
				meeting.channel,
				meeting.meeting,
				uploadData.uploadId,
				uploadData.cloudConvertUploadData,
				null,
				null,
				null,
				null,
			);
			dispatch(GraphicsActions.addGraphicsAction(
				token ?? '',
				graphic,
				() => {
					clearUploadProgress();
				},
			));
		}
	}

	function onUploadInit(name: string, type: string, identifier: string, request: XMLHttpRequest) {
		addUpload(name, type, identifier, request);
		setUploading(false);
		// Clear the file input selection. This is necessary to prompt
		// onChange if the uploader decides to upload the same file (i.e. cancels their
		// upload and then decides to upload the same file again)
		if (fileInputRef.current) {
			fileInputRef.current.value = '';
		}
	}

	const deleteMediaItem = useCallback(async (mediaItem: MediaItem) => {
		if (!token) return;
		clearUploadProgress();

		if (
			currentlyUploadingMedia?.id === mediaItem.id
			|| idOfCurrentlyUploadingMedia === mediaItem.cloud_convert_id
			|| mediaItem.id === 1
		) {
			dispatch(MediaActions.setCurrentlyUploadingMedia(undefined));
			dispatch(MediaActions.setIdOfCurrentlyUploadingMedia(''));
		}

		// selectedMedia.id is 1 if currently uploading directly to s3 (img/vid)
		if (mediaItem.id !== 1) {
			// Converting or already converted through cloud convert.
			// Delete from SQL and Cloud Convert if job still exists
			if (currentlyUploadingInModal.includes(mediaItem.id)) {
				dispatch(MediaActions.setCurrentlyUploadingInModal(
					currentlyUploadingInModal.filter((id) => id !== mediaItem.id),
				));
			}

			if (
				mediaItem.cloud_convert_id
				&& mediaItem.convert_status !== CLOUD_CONVERT_STATUS.COMPLETE
			) {
				await DeleteCloudConvertJob(token!, mediaItem.cloud_convert_id);
			}

			await DeleteMediaItem(token, mediaItem.id);

			if (socket) { socket.deleteMediaItem(mediaItem.id); }

			stripFeedFromShots(
				mediaItem!.id,
				AUDIO_TYPES.includes(mediaItem.type) ? FEED_TYPES.MEDIA_AUDIO : FEED_TYPES.MEDIA,
			);
		}
	}, [
		currentlyUploadingInModal,
		dispatch,
		socket,
		stripFeedFromShots,
		token,
		currentlyUploadingMedia,
		idOfCurrentlyUploadingMedia,
	]);

	async function handleUpload(e: React.ChangeEvent<HTMLInputElement>) {
		// FYI: we modified the file input to only allow one file at a
		// time because of some corner cases that got brought up
		// with converting vs not converting high bitrate mp4s,
		// CC only allowing one conversion at a time, etc.
		const { files: fileList } = e.target;

		if (fileList && fileList.length > 0) {
			try {
				setUploading(true);
				if (activeSubTabs.media === 'Files') {
					await doUploadMedia(
						false,
						fileList,
						onUploadInit,
					);
				} else if (activeSubTabs.media === 'Overlays' && meeting && token) {
					await doUploadOverlay(
						meeting,
						token,
						fileList,
						onUploadInit,
						onOverlayUploadSuccess,
					);
				}
			} catch (e1: any) {
				// eslint-disable-next-line no-console
				console.error(e1);
				clearUploadProgress();
			} finally {
				setUploading(false);
				if (fileInputRef.current) {
					fileInputRef.current.value = '';
				}
			}
		}
	}

	// function extractUserFromInvite(user1: any): any {
	// 	return user1!.profile;
	// }

	// const possibleUsers = useMemo(() => uniqueArray('gr_user', [
	// 	...invitations.map(extractUserFromInvite),
	// 	...connectedUsers,
	// ]), [connectedUsers, invitations]);

	const [yourShownMedia, roomShownMedia] = useMemo(() => {
		const baseMedia = [...files];

		let newYourShownMedia: MediaItem[] = [];
		const newRoomShownMedia: MediaItem[] = [];

		if (currentlyUploadingMedia) {
			newYourShownMedia = [currentlyUploadingMedia];
		}

		// we want to divide the media list my media | room media
		let i = 0;
		const len = baseMedia.length;
		for (i; i < len; i += 1) {
			// do not show capsule (stylepck) items in lists
			if (baseMedia[i]?.provider !== 'capsule') {
				// Added brandlive_profile so that pre-decoupling media would
				// display post_decoupling. But then realized during testing that we now display all
				// media for all users (see comment above) so not as relevant. But still leaving in.
				if (
					baseMedia[i].created_by === user?.gr_user
					|| (user!.brandlive_profile && baseMedia[i].created_by === user!.brandlive_profile)
				) {
					newYourShownMedia.push(baseMedia[i]);
				} else {
					newRoomShownMedia.push(baseMedia[i]);
				}
			}
		}

		return [newYourShownMedia, newRoomShownMedia];
	}, [user, files, currentlyUploadingMedia, isMediaFiltered]);

	const openMedia = useCallback((mediaId: number) => {
		const mediaItem = media.find((item) => item.id === Number(mediaId))!;
		dispatch(MediaActions.setMediaModalOpen(true, mediaItem));
	}, [dispatch, media]);

	const handleClick = useCallback((e: any) => {
		const path = e.composedPath();
		const len = path.length;
		let keepOpen = false;
		let isModalBackground = false;

		// Don't close the modal if anywhere in the modal is
		// clicked including the delete page dialog. Close the modal
		// if clicked outside (background)
		for (let i = 0; i < len; i += 1) {
			if (path[i].className?.includes) {
				if ((typeof path[i].className === 'string'
					&& (path[i].className.includes('media-modal') || path[i].className.includes('custom-graphics-modal'))
					&& !path[i].className.includes('media-modal-background'))
					|| path[i].className.includes('dialog-background')
					|| path[i].className.includes('dialog-container')
					|| path[i].className.includes('media-link')
					|| path[i].className.includes('screenshare-link')
					|| path[i].className.includes('media-slides-header')) {
					keepOpen = true;
					break;
				}
				if ((typeof path[i].className === 'string' && path[i].className.includes('media-modal-background'))) {
					// eslint-disable-next-line @typescript-eslint/no-unused-vars
					isModalBackground = true;
					break;
				}
			}
		}

		if (!keepOpen) {
			if (showGraphicModal) {
				dispatch({ type: HIDE_GRAPHIC_MODAL });
			} else {
				dispatch(MediaActions.setSelectedPage(1));
				dispatch(MediaActions.setMediaModalOpen(false, undefined));
			}
		}
	}, [mediaModalOpen, showGraphicModal]);

	useEffect(() => {
		if (mediaModalOpen || showGraphicModal) {
			window.addEventListener('mousedown', handleClick);
		}

		return () => {
			window.removeEventListener('mousedown', handleClick);
		};
	}, [handleClick, mediaModalOpen, showGraphicModal]);

	const deleteGraphicsItem = useCallback((graphics: GraphicInterface) => {
		if (!token || !meeting) return;
		dispatch(
			GraphicsActions.deleteGraphicsAction(
				token,
				graphics,
				() => { if (graphics.id) stripGraphicFromShots(graphics.id); },
			),
		);
	}, [dispatch, socket, stripFeedFromShots, stripGraphicFromShots, token]);

	const cancelItemUpload = useCallback(async (type: string, id: number, ccId: string) => {
		if (!token || !id || !socket) return;
		clearUploadProgress();

		if (type === UPLOAD_TYPES.MEDIA) {
			await DeleteMediaItem(token, id);
			socket.deleteMediaItem(id);
		}

		if (type === UPLOAD_TYPES.OVERLAY) {
			const overlay = graphicsArray.find((graphic: GraphicInterface) => graphic.id === id);
			if (overlay) {
				await deleteGraphicsItem(overlay);
			}
		}

		if (ccId) {
			await DeleteCloudConvertJob(token, ccId);
		}
	}, [dispatch, socket, token, graphicsArray]);

	const stopSharing = useCallback((mediaId: number) => {
		// reset media deck
		if (currentShot.shot_data.shotFeeds.some((feed: ShotFeed) => feed.media === mediaId)) {
			showAlert({
				message: 'Cannot stop sharing',
				description: 'This media file is currently live.',
				type: 'neutral',
				duration: 4000,
			});
			return;
		}

		dispatch(MediaActions.removePresentedMedia(mediaId));
	}, [presentedMedia, currentShot.shot_data.shotFeeds, socket, featureFlags, dispatch]);

	const getDeleteDialogPosition = (mediaLink: HTMLElement | null) => {
		let x = window.innerWidth - 450; let
			y = 20;	// default position if mediaLink happened to be null in the future
		if (mediaLink) {
			// Get x position. Delete dialog is always directly
			// left of the three dots button that opened it
			if (mediaLink.querySelector('.media-dots-wrap')) {
				x = mediaLink.querySelector('.media-dots-wrap')!.getBoundingClientRect().left - 410;
			} else {
				x = mediaLink.getBoundingClientRect().left - 74;
			}
			// Get y position. Delete dialog displays down from the three dots
			// button that opened it unless the media item is at the bottom
			// of the browser in which it displays upwards
			if ((mediaLink.getBoundingClientRect().bottom + 110) > window.innerHeight) {
				y = mediaLink.getBoundingClientRect().top - 116;
			} else {
				y = mediaLink.getBoundingClientRect().top + 31;
			}
		}
		return [x, y];
	};

	const showDeleteMediaItemDialog = useCallback((mediaItem: MediaItem, index: number) => {
		// Check if it's a recording or other media type
		const type = mediaItem.recording_download_link ? 'Recording' : 'File';

		setDeleteDialogXY(getDeleteDialogPosition(document.getElementById(`media-link-${index}`)));
		dialog.current.confirm(
			`Are you sure you want to delete this ${type}?`,
			'',
			'Delete',
			'Cancel',
			(confirmed: boolean) => {
				if (confirmed) {
					deleteMediaItem(mediaItem);
				}
			},
		);
	}, [deleteMediaItem]);

	const showDeleteOverlayDialog = useCallback((graphics: GraphicInterface, index: number) => {
		setDeleteDialogXY(getDeleteDialogPosition(document.getElementById(`media-link-${index}`)));
		dialog.current.confirm(
			'Are you sure you want to delete this Overlay?',
			'',
			'Delete',
			'Cancel',
			(confirmed: boolean) => {
				if (confirmed) {
					deleteGraphicsItem(graphics);
				}
			},
		);
	}, [deleteGraphicsItem]);

	const showCancelUploadDialog = (e: any, uploadingItem: UploadInterface, id : string) => {
		setDeleteDialogXY(getDeleteDialogPosition(document.getElementById(id)));

		dialog.current.confirm(
			'Are you sure you want to cancel this upload?',
			'',
			'Yes',
			'No',
			(confirmed: boolean) => {
				if (confirmed) {
					uploadingItem.uploadRequest.abort();
				}
			},
		);

		e?.stopPropagation();
	};

	const stopProcessingMediaUploadDialog = (e: any, id: string, {
		type,
		contentId,
		cloudConvertId,
	}: StopProcessingData) => {
		setDeleteDialogXY(getDeleteDialogPosition(document.getElementById(id)));

		dialog.current.confirm(
			'Are you sure you want to cancel this upload?',
			'',
			'Yes',
			'No',
			(confirmed: boolean) => {
				if (confirmed) {
					cancelItemUpload(type, contentId, cloudConvertId)
						// eslint-disable-next-line no-console
						.catch(console.error);
				}
			},
		);
		e.stopPropagation();
	};

	const downloadTitleOverlay = async (overlay: GraphicInterface) => {
		const {
			id,
			title: name,
		} = overlay;

		let {
			images: url,
		} = overlay;

		if (!id) return;

		if (url.endsWith('-mov-hls/master.m3u8')) {
			url = url.replace('-mov-hls/master.m3u8', '.mov');
		} else if (url.endsWith('-mp4-hls/master.m3u8')) {
			url = url.replace('-mp4-hls/master.m3u8', '.mp4');
		}

		setDownloadingOverlayIds((current) => [...current, id]);
		await downloadFile(url, name);
		setDownloadingOverlayIds((current) => (
			current.filter((currentId) => currentId !== id)
		));
	};

	const duplicateGraphicsItem = useCallback((graphic: GraphicInterface) => {
		if (!token || !graphic?.id) return;
		const content = generatedContent.find(
			(cont) => cont.fkTable === FkTable.graphics && cont.fkId === graphic?.id,
		);
		if (content?.jobStatus === ContentServiceJobStatus.processing) {
			showAlert({
				message: 'Cannot duplicate',
				description: 'The overlay is processing.',
				type: 'neutral',
				duration: 4000,
			});
			return;
		}
		dispatch(GraphicsActions.duplicateOverlay(token, graphic, content));
	}, [token, generatedContent]);

	const editGraphics = useCallback((graphic: GraphicInterface) => {
		setGraphicForModal(graphic);
		dispatch({
			type: SHOW_GRAPHIC_MODAL,
			payload: true,
		});
	}, [graphicsArray]);

	const presentMediaClicked = useCallback((mediaIdToPresent: number, page?: number) => {
		const presMedia = media.find((m: MediaItem) => m.id === mediaIdToPresent);
		const alreadyPresentedItem = presentedMedia.find(
			(m: PresentedMediaItem) => m.mediaId === mediaIdToPresent,
		);
		if (presMedia) {
			// Copy any data from the media item that is used during presenting
			const oldMedia = alreadyPresentedItem ?? {};
			const mediaToPresent: PresentedMediaItem = {
				mediaId: presMedia.id,
				currentPage: page ?? 1,
				...oldMedia,
			};
			dispatch(MediaActions.setSelectedPage(1));
			dispatch(MediaActions.addPresentedMedia(mediaToPresent));
			dispatch(MediaActions.setMediaModalOpen(false, undefined));
		}
	}, [presentedMedia, socket, user, featureFlags, media]);

	const convertToOverlay = async (mediaItem: MediaItem) => {
		if (meeting && user && token) {
			let movCCData: undefined | CloudConvertUploadData;
			let originalName = mediaItem.custom_name ?? mediaItem.original_name ?? '';
			let { images } = mediaItem;

			// if the file is an mov, we need to convert it to webm using cloud convert
			if (mediaItem.type === MEDIA_TYPES.MOV) {
				const tags = getTags('t:overlay');
				const prefix = buildMediaUrlPrefix(meeting.channel, meeting.meeting_id);
				movCCData = await ConvertMovToOverlay(
					token,
					prefix,
					mediaItem.images,
					tags,
					meeting.meeting,
				);
				originalName = `${mediaItem.original_name}.mov`;
				images = '';
			}

			const graphic = await buildOverlay(
				originalName,
				images,
				user.gr_user,
				meeting.channel,
				meeting.meeting,
				null,
				movCCData,
				null,
				null,
				null,
				null,
			);

			dispatch(GraphicsActions.addGraphicsAction(
				token,
				graphic,
				() => {
					deleteMediaItem(mediaItem);
					dispatch(setActiveSubTab(TABS.MEDIA, MediaTabOptionTypes.Overlays));
				},
			));
		}
	};

	const selectForSlideView = useCallback((mediaId: number | null) => {
		dispatch(MediaActions.setSlideViewMedia(mediaId));
	}, []);

	const openScreenShareDialog = () => {
		dispatch(VideoActions.setSharingScreen(!sharingScreen));
	};

	const toggleShareView = (media1: MediaItem) => {
		if (shareViewOpen) {
			setSelectedShareItem(null);
			setShareViewOpen(false);
			setTimeout(() => {
				setShareViewHidden(true);
			}, 150);
		} else {
			setSelectedShareItem(media1);
			setShareViewHidden(false);
			setTimeout(() => {
				setShareViewOpen(true);
			});
		}
	};

	const closeShareView = (currentlySelected?: number[]) => {
		if (currentlySelected && token && socket && selectedShareItem) {
			const updatedMedia: MediaItem = {
				...selectedShareItem,
				shared_with: currentlySelected.join(','),
				edited_by: user?.gr_user,
			};
			dispatch(MediaActions.saveMediaItem(token, updatedMedia, undefined));
			socket.mediaItemUpdate(updatedMedia);
		}
		setSelectedShareItem(null);
		setShareViewOpen(false);
		setTimeout(() => {
			setShareViewHidden(true);
		}, 150);
	};

	const invitedUsersNotInRoom = useMemo(() => {
		let returnArr: Invitation[] = [];
		if (roomState === ROOM_STATES.EDITING || isForShotModal) {
			// create fake feed for all invites that aren't in the room that have brandlive profiles
			returnArr = invitations.filter((invitation: Invitation) => !connectedUsers.some(
				(user1: any) => user1.gr_user === invitation.profile.gr_user && user1?.in_room,
			) && !invitation.watcher_only);
		}

		return returnArr;
	}, [invitations, roomState, isForShotModal, connectedUsers]);

	const [mediaOnCanvasIds, screensharesOnCanvasIds] = useMemo((): [number[], string[]] => {
		const mediaIds: number[] = [];
		const screenshareIds: string[] = [];
		const shot = isForShotModal
			? shotModalShot
			: currentShot;
		if (shot?.shot_data?.shotFeeds) {
			shot.shot_data.shotFeeds.forEach((feed) => {
				if (feed.media) {
					mediaIds.push(feed.media);
				} else if (feed.screenshare) {
					screenshareIds.push(feed.screenshare);
				}
			});
		}
		return [mediaIds, screenshareIds];
	}, [currentShot.shot_data.shotFeeds, shotModalShot, isForShotModal]);

	const graphicIsOnCanvas = useCallback((graphic: GraphicInterface) => {
		if (graphic.is_global) {
			return true;
		}

		const shot = isForShotModal ? shotModalShot : currentShot;

		if (!shot.shot_data.shotGraphics || !graphic.id) {
			return false;
		}

		if (shot.shot_data.shotGraphics.includes(graphic.id)) {
			return true;
		}
		return false;
	}, [currentShot.shot_data.shotGraphics,
		isForShotModal,
		shotModalShot.shot_data.shotGraphics]);

	// A footer to act as bottom padding for the media list
	const footer: JSX.Element = (
		<div className='media-footer' />
	);

	const displayedYourMedia = [
		...mediaUploads.map((mediaUpload: UploadInterface, index: number) => (
			<UploadingLink
				key={index}
				uploadingItem={mediaUpload}
				id={`uploading-link-media-${index}`}
				cancelUpload={showCancelUploadDialog}
			/>
		)),
		...yourShownMedia.map((mediaItem: MediaItem, index: number) => (
			<MediaLink
				index={index}
				onPreview={() => openMedia(mediaItem.id)}
				onDelete={() => showDeleteMediaItemDialog(mediaItem, index)}
				onDownload={(downloadType: string) => downloadMedia(
					mediaItem,
					downloadType,
					() => { setIdOfMediaBeingDownloaded(mediaItem.id); },
					() => { setIdOfMediaBeingDownloaded(0); },
				)}
				onConvertToOverlay={() => convertToOverlay(mediaItem)}
				cancelMediaItemUpload={() => {
					cancelItemUpload(UPLOAD_TYPES.MEDIA, mediaItem.id, mediaItem.cloud_convert_id ?? '');
				}}
				mediaItem={mediaItem}
				user={user}
				key={`${mediaItem.id}_${mediaItem.title}`}
				progress={progress}
				currentlyPresented={presentedMedia.filter((med) => med.mediaId === mediaItem.id)[0]}
				isHost={host}
				thisMediaIsDownloading={mediaItem.id === idOfMediaBeingDownloaded}
				roomState={roomState}
				selectForSlideView={selectForSlideView}
				isForShotModal={isForShotModal}
				currentShot={currentShot}
				toggleShareView={toggleShareView}
				isOnCanvas={mediaOnCanvasIds.includes(mediaItem.id)}
			/>
		)),
	];

	const displayRoomShownMedia = ([
		...roomShownMedia.map((mediaItem: MediaItem, index: number) => (
			<MediaLink
				index={index}
				onPreview={() => openMedia(mediaItem.id)}
				onDelete={() => showDeleteMediaItemDialog(mediaItem, index)}
				onDownload={(downloadType: string) => downloadMedia(
					mediaItem,
					downloadType,
					() => { setIdOfMediaBeingDownloaded(mediaItem.id); },
					() => { setIdOfMediaBeingDownloaded(0); },
				)}
				onConvertToOverlay={() => convertToOverlay(mediaItem)}
				cancelMediaItemUpload={() => {
					cancelItemUpload(UPLOAD_TYPES.MEDIA, mediaItem.id, mediaItem.cloud_convert_id ?? '');
				}}
				mediaItem={mediaItem}
				user={user}
				key={`${mediaItem.id}_${mediaItem.title}`}
				progress={progress}
				currentlyPresented={presentedMedia.filter((med) => med.mediaId === mediaItem.id)[0]}
				isHost={host}
				thisMediaIsDownloading={mediaItem.id === idOfMediaBeingDownloaded}
				roomState={roomState}
				selectForSlideView={selectForSlideView}
				isForShotModal={isForShotModal}
				currentShot={currentShot}
				toggleShareView={toggleShareView}
				isOnCanvas={mediaOnCanvasIds.includes(mediaItem.id)}
			/>
		)),
		footer,
	]);

	const displayedFilteredMedia = filteredMedia.map((mediaItem: MediaItem, index: number) => (
		<MediaLink
			index={index}
			onPreview={() => openMedia(mediaItem.id)}
			onDelete={() => showDeleteMediaItemDialog(mediaItem, index)}
			onDownload={(downloadType: string) => downloadMedia(
				mediaItem,
				downloadType,
				() => { setIdOfMediaBeingDownloaded(mediaItem.id); },
				() => { setIdOfMediaBeingDownloaded(0); },
			)}
			onConvertToOverlay={() => convertToOverlay(mediaItem)}
			cancelMediaItemUpload={() => {
				cancelItemUpload(UPLOAD_TYPES.MEDIA, mediaItem.id, mediaItem.cloud_convert_id ?? '');
			}}
			mediaItem={mediaItem}
			user={user}
			key={`${mediaItem.id}_${mediaItem.title}`}
			progress={progress}
			currentlyPresented={presentedMedia.filter((med) => med.mediaId === mediaItem.id)[0]}
			isHost={host}
			thisMediaIsDownloading={mediaItem.id === idOfMediaBeingDownloaded}
			roomState={roomState}
			selectForSlideView={selectForSlideView}
			isForShotModal={isForShotModal}
			currentShot={currentShot}
			toggleShareView={toggleShareView}
			isOnCanvas={mediaOnCanvasIds.includes(mediaItem.id)}
		/>
	));

	const displayedFilteredGraphics = filteredGraphics?.map(
		(graphic: GraphicInterface, index: number) => (
			<GraphicsLink
				index={index}
				graphic={graphic}
				isHost={host}
				onDelete={() => showDeleteOverlayDialog(graphic, index)}
				onDownload={() => downloadTitleOverlay(graphic)}
				onDuplicate={() => duplicateGraphicsItem(graphic)}
				onEdit={() => editGraphics(graphic)}
				onCancelConvert={(e: any) => {
					stopProcessingMediaUploadDialog(
						e,
						`media-link-${index}`,
						{
							type: UPLOAD_TYPES.OVERLAY,
							contentId: graphic?.id ?? 0,
							cloudConvertId: graphic.cloud_convert_id ?? '',
						},
					);
				}}
				user={user}
				key={`${graphic.original_name}_${graphic.id}`}
				isForShotModal={isForShotModal}
				isOnCanvas={graphicIsOnCanvas(graphic)}
				isDownloading={downloadingOverlayIds.includes(graphic.id ?? 0)}
			/>
		),
	) ?? [];

	const displayFilteredRecordings = filteredRecordings.map(
		(recording: MediaItem, index: number) => (
		<MediaLink
			index={index}
			onPreview={() => openMedia(recording.id)}
			onDelete={() => showDeleteMediaItemDialog(recording, index)}
				onDownload={(downloadType: string) => downloadMedia(
					recording,
					downloadType,
					() => { setIdOfMediaBeingDownloaded(recording.id); },
					() => { setIdOfMediaBeingDownloaded(0); },
				)}
			onConvertToOverlay={() => convertToOverlay(recording)}
			cancelMediaItemUpload={() => {}}
			mediaItem={recording}
			user={user}
			key={`${recording.id}_${recording.title}`}
			progress={progress}
			currentlyPresented={presentedMedia.filter((med) => med.mediaId === recording.id)[0]}
			isHost={host}
			thisMediaIsDownloading={recording.id === idOfMediaBeingDownloaded}
			roomState={roomState}
			selectForSlideView={selectForSlideView}
			isForShotModal={isForShotModal}
			currentShot={currentShot}
			toggleShareView={toggleShareView}
			isOnCanvas={mediaOnCanvasIds.includes(recording.id)}
		/>
		),
	);

	const getVirtuosoComponent = (items: any[], mediaIsFiltered: boolean = false) => {
		const nItems = [...items, footer];

		return <div className="virtuoso-scroller">
			<GroupedVirtuoso
				groupCounts={[nItems.length]}
				groupContent={() => (
					items.length < 1
						? placeholderListElement
						: <div className={classNames('media-group-divider virtuoso-scroller-glow', {
							'less-padding': mediaIsFiltered,
						})}/>
				)}
				itemContent={(index) => nItems[index] || placeholderListElement}
			/>
		</div>;
	};

	const userScreenShares = [
		...connectedUsers.filter(
			(user2: ConnectedUser) => !user2.watcher_only && user2?.in_room,
		).map((user3: ConnectedUser, index: number) => (
			<ScreenShareLink
				key={index}
				connectedPresenter={user3}
				isOnCanvas={screensharesOnCanvasIds.includes(user3.email)}
			/>
		)),
		...invitedUsersNotInRoom.map(({ profile }: Invitation, index: number) => (
			<ScreenShareLink
				key={index}
				invitedPresenter={profile}
				isOnCanvas={screensharesOnCanvasIds.includes(profile.email)}
			/>
		)),
	];

	return (
		<>
			{!slideViewMedia && !selectedShareItem
				&& <div className="media-wrapper height-full">
					<div className="media-container height-full flex-column">
						<SlidingMenu
							options={mediaTabOptions}
							selectedOption={mediaTabOptions.find(
								(op: SlidingMenuOption) => op.label === activeSubTabs.media,
							) ?? null}
							callback={
								(option: SlidingMenuOption) => dispatch(setActiveSubTab(TABS.MEDIA, option.label))
							}
							className='position-relative width-calc-full'
							fullWidthDivider={true}
						/>

						{activeSubTabs.media !== MEDIA_TABS.SCREENSHARES
							&& <>
								<input
									id="file-input"
									ref={fileInputRef}
									type="file"
									name="name"
									style={{ display: 'none' }}
									onChange={handleUpload}
									accept={ALL_FILE_TYPES
										.map((a) => `.${a}`)
										.join(',')
									}
								/>
								<MediaMenu
									uploading={uploading}
									filteredMedia={filteredMedia}
									filteredRecordings={filteredRecordings}
									filteredGraphics={filteredGraphics}
									handleIsMediaFiltered={(isFiltered: boolean) => setIsMediaFiltered(isFiltered)}
									onFilter={(results: MediaSearchResults) => {
										// do not return capsule (stylepack) items from search
										setFilteredMedia(results.files.filter(filterCapsule));
										setFilteredRecordings(results.recordings.filter(filterCapsule));
										setFilteredGraphics(results.graphics.filter(filterCapsule));
									}}
									media={files}
									recordings={recordings}
									graphics={graphicsArray}
									subtab={activeSubTabs.media ?? ''}
									isForModal={isForShotModal}
								/>
							</>
						}

						{activeSubTabs.media === MEDIA_TABS.FILES
							&& !isMediaFiltered
							&& <div className="virtuoso-scroller">
								<GroupedVirtuoso
									groupCounts={[displayedYourMedia.length, displayRoomShownMedia.length]}
									groupContent={(index) => {
										const sectionMediaLength = index === 0
											? displayedYourMedia.length
											: displayRoomShownMedia.length - 1; // footer element
										if (sectionMediaLength < 1) return placeholderListElement;

										return <h6 className='media-group-divider grouped bl-font-header virtuoso-scroller-glow' style={{
											marginTop: index === 0 ? 0 : 2,
										}}>
											{index === 0 ? 'My Media' : 'Room Media'}
										</h6>;
									}}
									itemContent={(index, groupIndex) => {
										const actualIndex = groupIndex === 0
											? index
											: index - displayedYourMedia.length;
										const item: JSX.Element = groupIndex === 0
											? displayedYourMedia[actualIndex]
											: displayRoomShownMedia[actualIndex];
										return item || placeholderListElement;
									}}
								/>
							</div>
						}

						{activeSubTabs.media === MEDIA_TABS.FILES
							&& isMediaFiltered
							&& displayedFilteredMedia.length > 0
							&& getVirtuosoComponent(displayedFilteredMedia, isMediaFiltered)
						}

						{activeSubTabs.media === MEDIA_TABS.SCREENSHARES && (
							<>
								<div className="screenshare-container">
									<button className={classNames('start-screenshare-button background-light padding-medium round text-white', sharingScreen && 'sharing', RESTRICTED_CONNECTION_TYPES.includes(restrictedUserState) && 'disabled')} onClick={openScreenShareDialog} >
										<div className='button-content align-items-center'>
											<Icon
												name={sharingScreen ? ICONS.SQUARE : ICONS.MEDIA_SHARE}
												color={sharingScreen ? COLORS.WHITE : COLORS.DEFAULT_GRAY}
												size={sharingScreen ? 10 : 18}
											/>
											<span className='text-transform-capitalize font-weight-heavy white-space-nowrap font-weight-heavy'>{sharingScreen ? 'stop sharing' : 'share'} screen</span>
										</div>
									</button>
								</div>
								{host ? getVirtuosoComponent(userScreenShares) : null}
							</>
						)}

						{activeSubTabs.media === MEDIA_TABS.OVERLAYS
							&& (displayedFilteredGraphics?.length > 0 || overlayUploads.length > 0)
							&& getVirtuosoComponent(
								[
									...!isMediaFiltered
										? overlayUploads.map((overlayUpload: UploadInterface, index: number) => (
											<UploadingLink
												key={index}
												uploadingItem={overlayUpload}
												cancelUpload={showCancelUploadDialog}
												id={`uploading-link-overlay-${index}`}
											/>
										))
										: [],
									...displayedFilteredGraphics,
								],
								isMediaFiltered,
							)
						}

						{activeSubTabs.media === MEDIA_TABS.RECORDINGS
							&& getVirtuosoComponent(displayFilteredRecordings, isMediaFiltered)
						}
					</div>

				</div>
			}

			{
				slideViewMedia
				&& <MediaSlidesView
					socket={socket}
					mediaId={slideViewMedia}
					clearMediaSelectedForSlideView={() => dispatch(MediaActions.setSlideViewMedia(null))}
					openMediaModal={(id: number) => openMedia(id)}
					user={user}
					isHost={host}
					onPresent={presentMediaClicked}
					stopPresenting={stopSharing}
				/>
			}

			{selectedShareItem && (
				<div className="media-share-container">
					<ShareList
						shareListOpen={shareViewOpen}
						shareListHidden={shareViewHidden}
						closeShareList={closeShareView}
						selectedItem={selectedShareItem}
						creatorId={selectedShareItem?.created_by}
						shareType={SHARE_TYPE.MEDIA}
						forMediaDrawer={true}
					/>
				</div>
			)}

			{
				mediaModalOpen
				&& <Portal targetId="media-modal-portal">
					<div className="media-modal-background">
						<MediaModal
							socket={socket}
							addUpload={addUpload}
						/>
					</div>
				</Portal>
			}

			{
				showGraphicModal && graphicForModal && (
					<Portal targetId="media-modal-portal">
						<div className="media-modal-background">
							<CustomGraphicsModal
								graphicForGraphicModal={graphicForModal}
							/>
						</div>
					</Portal>
				)
			}

			<Dialog ref={dialog} xPixel={deleteDialogXY[0]} yPixel={deleteDialogXY[1]} width={425} />
		</>
	);
}
